import { createId, deepClone } from '@prompt-optimize/shared'
import type {
  MjParams,
  PromptDocument,
  Section,
  Tag,
} from './types'

export function createTag(raw: string, weight?: number): Tag {
  const t: Tag = { id: createId(), raw: raw.trim() }
  if (weight !== undefined && weight !== 1)
    t.weight = weight
  return t
}

export function createSection(title: string, order: number): Section {
  return {
    id: createId(),
    title: title.trim() || 'Untitled',
    type: 'tags',
    tags: [],
    enabled: true,
    order,
  }
}

export function createEmptyDocument(name = 'Untitled'): PromptDocument {
  return {
    id: createId(),
    name,
    schemaVersion: 1,
    sections: [],
    params: {},
    updatedAt: Date.now(),
  }
}

/**
 * Duplicate an existing document. Deep-clones sections (including all tags)
 * and params, assigns brand-new ids so the copy is fully independent.
 */
export function duplicateDocument(source: PromptDocument, name?: string): PromptDocument {
  const sections: Section[] = source.sections.map((s, idx) => ({
    ...deepClone(s),
    id: createId(),
    order: idx,
    tags: s.tags.map(t => ({ ...deepClone(t), id: createId() })),
  }))
  return {
    id: createId(),
    name: name ?? `${source.name} (副本)`,
    schemaVersion: 1,
    sections,
    params: deepClone(source.params),
    updatedAt: Date.now(),
  }
}

/**
 * Reorder sections and rewrite the `order` field to be contiguous [0..n-1].
 */
export function normalizeSectionOrder(sections: Section[]): Section[] {
  return sections
    .slice()
    .sort((a, b) => a.order - b.order)
    .map((s, i) => ({ ...s, order: i }))
}

/**
 * Sanitize MJ params: drop nullish/empty values, clamp numeric ranges.
 */
export function sanitizeMjParams(p: MjParams): MjParams {
  const out: MjParams = {}
  if (p.ar && p.ar.trim())
    out.ar = p.ar.trim()
  if (p.v && p.v.trim())
    out.v = p.v.trim()
  if (p.style && p.style.trim())
    out.style = p.style.trim()
  if (typeof p.q === 'number' && !Number.isNaN(p.q))
    out.q = p.q
  if (typeof p.s === 'number' && !Number.isNaN(p.s))
    out.s = Math.min(1000, Math.max(0, p.s))
  if (typeof p.chaos === 'number' && !Number.isNaN(p.chaos))
    out.chaos = Math.min(100, Math.max(0, p.chaos))
  if (p.no && p.no.length) {
    const cleaned = p.no.map(n => n.trim()).filter(Boolean)
    if (cleaned.length)
      out.no = cleaned
  }
  return out
}
